import React, { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import { FaSpinner, FaEye, FaUtensils, FaCalendarAlt, FaUser, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { showModal } from "../../utils/modalUtility";

const customStyles = `
  .recipe-card {
    border: none;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.15);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    overflow: hidden;
  }
  .recipe-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 32px rgba(149, 157, 165, 0.25);
  }
  .card-header-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #e0e7ff, #ede9fe);
    color: #4f46e5;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin: 0 auto 15px auto;
  }
  .click-badge {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.5rem;
    display: inline-block;
    line-height: 1;
  }
  .btn-premium {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 12px;
    font-weight: 600;
    transition: all 0.2s ease;
  }
  .btn-premium:hover {
    background: linear-gradient(135deg, #4338ca, #6d28d9);
    color: white;
    transform: scale(1.03);
    box-shadow: 0 8px 16px rgba(124, 58, 237, 0.3);
  }
  .modal-custom-header {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: white;
    border-bottom: none;
  }
  .list-group-custom .list-group-item {
    border: 1px solid #f1f5f9;
    margin-bottom: 10px;
    border-radius: 12px !important;
    background: #f8fafc;
    box-shadow: 0 2px 8px rgba(0,0,0,0.02);
    transition: all 0.2s ease;
  }
  .list-group-custom .list-group-item:hover {
    background: #ffffff;
    border-color: #e2e8f0;
    transform: translateX(4px);
  }
  .page-header-gradient {
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid #e2e8f0;
  }
`;

const RecipeClicks = () => {
  const [loading, setLoading] = useState(false);
  const [recipeClicks, setRecipeClicks] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortOrder, setSortOrder] = useState("DESC");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const axiosAPI = useAxios();

  const fetchRecipeClicks = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      const endpoint = `/api/recipe-clicks/all?${params.toString()}`;

      const response = await axiosAPI.get(endpoint);
      const data = response?.data?.data || [];

      const grouped = data.reduce((acc, item) => {
        const name = item.name || item.recipe_name || "Unknown";
        const entityId = item.recipe_id || item.id;
        const key = entityId ? `${entityId}-${name}` : name;
        if (!acc[key]) {
          acc[key] = {
            id: item.id,
            recipe_id: item.recipe_id || item.id,
            name: name,
            video_id: item.video_id || item.video_url || item.youtube_url || item.url || "",
            total_clicks: item.total_clicks || 0,
            views: []
          };
        } else {
          acc[key].total_clicks = Math.max(acc[key].total_clicks, item.total_clicks || 0);
          if (!acc[key].video_id) acc[key].video_id = item.video_id || item.video_url || item.youtube_url || item.url || "";
        }
        if (item.view) {
          acc[key].views.push({
            date: item.view,
            user_id: item.user_id || null,
            user_name: item.user_name || null,
            user_phone: item.user_phone || null,
            user_email: item.user_email || null,
            user_city: item.user_city || null,
            user_role: item.user_role || null,
            user_created_at: item.user_created_at || null,
          });
        }
        return acc;
      }, {});

      const groupedList = Object.values(grouped).map(rc => ({
        ...rc,
        views: (rc.views || []).sort((a, b) => new Date(b.date) - new Date(a.date))
      }));

      setRecipeClicks(groupedList);
    } catch (error) {
      console.error("Error fetching recipe clicks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipeClicks();
  }, [startDate, endDate]);

  const handleViewClick = (recipe) => {
    setSelectedRecipe(recipe);
    setSelectedUser(null);
    showModal("viewRecipeClicksModal");
  };

  const sortedRecipeClicks = [...recipeClicks].sort((a, b) => {
    if (sortOrder === "DESC") {
      return b.total_clicks - a.total_clicks;
    }
    return a.total_clicks - b.total_clicks;
  });

  return (
    <div className="container py-4">
      <style>{customStyles}</style>
      <div className="page-inner">

        <div className="page-header-gradient d-flex flex-wrap justify-content-between align-items-center shadow-sm gap-3">
          <div>
            <h3 className="fw-bold mb-1" style={{ color: "#1e293b" }}>Recipe Insights</h3>
            <p className="text-muted mb-0">Track and analyze user engagement across all your recipes.</p>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">

            <select
              className="form-select shadow-sm bg-white text-dark"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={{ minWidth: "150px", borderRadius: "12px", border: "1px solid #e2e8f0" }}
            >
              <option value="DESC">Highest Clicks</option>
              <option value="ASC">Lowest Clicks</option>
            </select>

            <div className="d-flex align-items-center gap-2">
              <input
                type="date"
                className="form-control shadow-sm bg-white text-dark"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
              />
              <span className="text-muted fw-bold">to</span>
              <input
                type="date"
                className="form-control shadow-sm bg-white text-dark"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
              />
            </div>

            <span className="badge bg-white text-dark shadow-sm px-3 py-2 rounded-pill fs-6 border">
              Total Recipes:
              <span className="text-primary fw-bold ms-1">{recipeClicks.length}</span>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="text-center my-5 py-5">
            <FaSpinner className="spinner fs-1 text-primary mb-3" spin />
            <h5 className="text-muted">Crunching the data...</h5>
          </div>
        ) : sortedRecipeClicks.length === 0 ? (
          <div className="text-center my-5 py-5 bg-white rounded-4 shadow-sm border border-light">
            <img src="https://cdn-icons-png.flaticon.com/512/7486/7486747.png" alt="No data" width="120" className="opacity-50 mb-4" />
            <h4 className="text-muted fw-bold">No Engagement Data Yet</h4>
            <p className="text-muted">When users click on recipes, their activity will appear here.</p>
          </div>
        ) : (
          <div className="row g-4">
            {sortedRecipeClicks.map((item, index) => (
              <div className="col-lg-4 col-md-6" key={index}>
                <div className="recipe-card h-100 d-flex flex-column p-4 border border-light">
                  <div className="card-header-icon">
                    <FaUtensils />
                  </div>
                  <div className="text-center flex-grow-1">
                    <h5 className="fw-bold mb-1" style={{ color: "#334155" }}>{item.name}</h5>
                    <p className="text-muted small mb-4 mt-2">Total Engagement</p>

                    <div className="mb-4">
                      <div className="click-badge">
                        {item.total_clicks}
                      </div>
                      <div className="text-muted fw-semibold small mt-1">TOTAL CLICKS</div>
                    </div>
                  </div>

                  <div className="mt-auto pt-3 border-top">
                    <button
                      className="btn btn-premium w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={() => handleViewClick(item)}
                    >
                      <FaEye /> View History
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Recipe Clicks Modal */}
      <div
        className="modal fade"
        id="viewRecipeClicksModal"
        tabIndex="-1"
        aria-labelledby="viewRecipeClicksModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
            <div className="modal-header modal-custom-header px-4 py-3">
              <div>
                <h5 className="modal-title fw-bold mb-0" id="viewRecipeClicksModalLabel">
                  Engagement History
                </h5>
                <small className="text-white-50">{selectedRecipe?.name}</small>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-4 bg-light">
              {/* Detailed User Information Card (When user is clicked) */}
              {selectedUser && (
                <div className="card border-0 shadow-sm rounded-4 mb-3" style={{ background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)", border: "1.5px solid #86efac" }}>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <div className="bg-primary text-white rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm" style={{ width: 38, height: 38 }}>
                          <FaUser style={{ fontSize: "1rem" }} />
                        </div>
                        <div>
                          <h6 className="fw-bold text-dark mb-0">{selectedUser.user_name || "User Details"}</h6>
                          <small className="text-muted">User ID: #{selectedUser.user_id || "N/A"}</small>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-outline-secondary rounded-circle" 
                        style={{ width: 26, height: 26, padding: 0, lineHeight: 1 }}
                        onClick={() => setSelectedUser(null)}
                        title="Close profile card"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="row g-2 pt-2 border-top mt-1">
                      <div className="col-6">
                        <small className="text-muted d-block fw-semibold" style={{ fontSize: "0.72rem" }}>MOBILE NUMBER</small>
                        <span className="fw-bold text-dark small">{selectedUser.user_phone || "Not Provided"}</span>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block fw-semibold" style={{ fontSize: "0.72rem" }}>EMAIL ADDRESS</small>
                        <span className="fw-semibold text-dark small">{selectedUser.user_email || "Not Provided"}</span>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block fw-semibold" style={{ fontSize: "0.72rem" }}>CITY</small>
                        <span className="fw-semibold text-dark small">{selectedUser.user_city || "Not Provided"}</span>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block fw-semibold" style={{ fontSize: "0.72rem" }}>ROLE</small>
                        <span className="badge bg-light text-dark border small">{selectedUser.user_role || "User"}</span>
                      </div>
                      {selectedUser.user_created_at && (
                        <div className="col-12">
                          <small className="text-muted d-block fw-semibold" style={{ fontSize: "0.72rem" }}>ACCOUNT CREATED</small>
                          <span className="text-muted small">{selectedUser.user_created_at}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {selectedRecipe?.views?.length > 0 ? (
                <ul className="list-group list-group-custom">
                  {selectedRecipe.views.map((v, idx) => {
                    const date = typeof v === "object" ? v.date : v;
                    const userName = typeof v === "object" ? v.user_name : null;
                    const userPhone = typeof v === "object" ? v.user_phone : null;
                    const userEmail = typeof v === "object" ? v.user_email : null;
                    const hasUser = userName || userPhone || userEmail || (typeof v === "object" && v.user_id);
                    return (
                      <li className="list-group-item d-flex justify-content-between align-items-center px-4 py-3" key={idx}>
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-2 d-flex align-items-center justify-content-center">
                            <FaCalendarAlt />
                          </div>
                          <div>
                            <div className="fw-semibold text-secondary">Click #{selectedRecipe.views.length - idx}</div>
                            {hasUser ? (
                              <div className="mt-1">
                                <div 
                                  className="p-1.5 px-2 rounded-2 border bg-white d-inline-flex align-items-center gap-2 cursor-pointer shadow-sm"
                                  style={{ cursor: "pointer", transition: "all 0.15s ease", border: "1px solid #e2e8f0" }}
                                  onClick={() => setSelectedUser(v)}
                                  title="Click to view full user information"
                                >
                                  <FaUser className="text-primary" style={{ fontSize: "0.75rem" }} />
                                  <div>
                                    <div className="fw-bold text-dark d-flex align-items-center gap-1" style={{ fontSize: "0.85rem" }}>
                                      {userName || "User"}
                                      <span className="badge bg-primary text-white ms-1" style={{ fontSize: "0.6rem", padding: "1px 5px", borderRadius: "4px" }}>
                                        Info
                                      </span>
                                    </div>
                                    {userPhone && (
                                      <small className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.76rem" }}>
                                        <FaPhoneAlt className="text-secondary" style={{ fontSize: "0.6rem" }} />
                                        {userPhone}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="badge bg-light text-muted border px-2 py-0.5 mt-1" style={{ fontSize: "0.72rem" }}>
                                Guest Visitor
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="badge bg-white text-dark shadow-sm px-3 py-2 border rounded-pill">{date}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center py-5">
                  <h6 className="text-muted mb-0">No historical timestamp data available.</h6>
                </div>
              )}
            </div>
            <div className="modal-footer bg-white border-top px-4 py-3">
              <button
                type="button"
                className="btn btn-light fw-bold px-4"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeClicks;
